/**
 * NginxPanel — управление nginx: список сайтов, редактор конфигов
 * (через HostFileBrowser-подход), nginx -t, reload, certbot, логи.
 *
 * Backend: /api/v1/devops/nginx/{sites,test,reload,site/enable,site/disable,cert,log},
 * /api/v1/devops/files/{read,write}.
 */

import { lazy, Suspense, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  Save,
  Power,
  PowerOff,
  ShieldCheck,
  FileText,
  Globe,
  Plus,
} from "lucide-react";
import {
  fetchNginxSites,
  nginxTest,
  nginxReload,
  nginxSiteToggle,
  nginxCert,
  fetchNginxLog,
  fetchHostFile,
  writeHostFile,
  type NativeNginxSite,
} from "../../lib/api";
import { cn } from "../../lib/cn";

const CodeMirrorEditor = lazy(() => import("../files/CodeMirrorEditor"));

type Section = "sites" | "logs" | "cert";

export function NginxPanel() {
  const [section, setSection] = useState<Section>("sites");

  return (
    <div className="space-y-3">
      <div className="flex gap-1 flex-wrap">
        <SectionBtn
          active={section === "sites"}
          onClick={() => setSection("sites")}
          icon={<Globe className="size-3.5" />}
          label="Сайты"
        />
        <SectionBtn
          active={section === "logs"}
          onClick={() => setSection("logs")}
          icon={<FileText className="size-3.5" />}
          label="Логи"
        />
        <SectionBtn
          active={section === "cert"}
          onClick={() => setSection("cert")}
          icon={<ShieldCheck className="size-3.5" />}
          label="HTTPS-сертификат"
        />
      </div>
      {section === "sites" && <SitesSection />}
      {section === "logs" && <LogsSection />}
      {section === "cert" && <CertSection />}
    </div>
  );
}

function SectionBtn({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 rounded-md text-small inline-flex items-center gap-1.5 border transition-colors",
        active
          ? "bg-accent-500/10 text-accent-500 border-accent-500/40"
          : "border-border/60 text-fg-muted hover:bg-bg-subtle",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function SitesSection() {
  const [sites, setSites] = useState<NativeNginxSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [openSite, setOpenSite] = useState<{ name: string; path: string; content: string; original: string; dirty: boolean } | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; output: string } | null>(null);

  async function load() {
    try {
      setError(null);
      const r = await fetchNginxSites();
      setSites(r.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function doToggle(s: NativeNginxSite) {
    setBusy(`toggle:${s.name}`);
    try {
      await nginxSiteToggle(s.name, !s.enabled);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function doTest() {
    setBusy("test");
    setTestResult(null);
    try {
      const r = await nginxTest();
      setTestResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function doReload() {
    setBusy("reload");
    setTestResult(null);
    try {
      const r = await nginxReload();
      setTestResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function openConfig(s: NativeNginxSite) {
    const path = s.available_path || s.enabled_path;
    if (!path) return;
    try {
      const f = await fetchHostFile(path);
      setOpenSite({
        name: s.name,
        path,
        content: f.content,
        original: f.content,
        dirty: false,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function saveConfig() {
    if (!openSite) return;
    setBusy("save");
    try {
      await writeHostFile(openSite.path, openSite.content);
      setOpenSite({ ...openSite, original: openSite.content, dirty: false });
      // Сразу прогоним nginx -t
      const r = await nginxTest();
      setTestResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="grid xl:grid-cols-[400px_1fr] gap-3">
      <div className="space-y-2 min-h-0 flex flex-col">
        <div className="flex gap-1 flex-wrap">
          <button
            type="button"
            onClick={doTest}
            disabled={busy === "test"}
            className="px-2 py-1 rounded text-[12px] border border-border/60 hover:bg-bg-subtle inline-flex items-center gap-1 disabled:opacity-60"
          >
            {busy === "test" ? <Loader2 className="size-3 animate-spin" /> : <ShieldCheck className="size-3" />}
            nginx -t
          </button>
          <button
            type="button"
            onClick={doReload}
            disabled={busy === "reload"}
            className="px-2 py-1 rounded text-[12px] border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 inline-flex items-center gap-1 disabled:opacity-60"
          >
            {busy === "reload" ? <Loader2 className="size-3 animate-spin" /> : <RefreshCw className="size-3" />}
            reload
          </button>
          <button
            type="button"
            onClick={load}
            className="px-2 py-1 rounded text-[12px] border border-border/60 hover:bg-bg-subtle text-fg-muted"
          >
            <RefreshCw className="size-3" />
          </button>
        </div>
        {testResult && (
          <div
            className={cn(
              "rounded border p-2 text-[11px] font-mono whitespace-pre-wrap",
              testResult.success ? "border-success/40 bg-success/5 text-success" : "border-danger/40 bg-danger/5 text-danger",
            )}
          >
            <div className="flex items-center gap-1 font-bold mb-1">
              {testResult.success ? <CheckCircle2 className="size-3.5" /> : <XCircle className="size-3.5" />}
              {testResult.success ? "OK" : "FAIL"}
            </div>
            {testResult.output.slice(0, 1500)}
          </div>
        )}
        {error && <div className="rounded border border-danger/40 bg-danger/5 p-2 text-small text-danger">{error}</div>}
        <div className="flex-1 min-h-0 rounded-modal border border-border/60 bg-bg-card/40 overflow-auto">
          {loading ? (
            <div className="p-3 text-fg-muted text-small">
              <Loader2 className="size-4 animate-spin inline mr-2" /> загрузка…
            </div>
          ) : (
            <ul className="text-small">
              {sites.map((s) => (
                <li
                  key={s.name}
                  className="flex items-center gap-1 px-2 py-1.5 hover:bg-bg-subtle/50 border-b border-border/20"
                >
                  <span
                    className={cn(
                      "size-1.5 rounded-full shrink-0",
                      s.enabled ? "bg-success" : "bg-fg-subtle",
                    )}
                  />
                  <button
                    type="button"
                    className="flex-1 text-left font-mono truncate hover:underline"
                    onClick={() => openConfig(s)}
                  >
                    {s.name}
                  </button>
                  <span className="text-[10px] text-fg-subtle tabular-nums">{s.size}b</span>
                  <button
                    type="button"
                    onClick={() => doToggle(s)}
                    disabled={busy === `toggle:${s.name}`}
                    title={s.enabled ? "disable" : "enable"}
                    className={cn(
                      "p-1 rounded border text-[11px] inline-flex items-center disabled:opacity-50",
                      s.enabled
                        ? "border-warning/40 text-warning hover:bg-warning/10"
                        : "border-accent-500/40 text-accent-500 hover:bg-accent-500/10",
                    )}
                  >
                    {busy === `toggle:${s.name}` ? <Loader2 className="size-3 animate-spin" /> : s.enabled ? <PowerOff className="size-3" /> : <Power className="size-3" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden flex flex-col min-h-[500px]">
        {!openSite ? (
          <div className="flex-1 grid place-items-center text-fg-muted text-small">
            Кликни сайт слева, чтобы редактировать nginx-конфиг.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border/40 bg-bg-subtle/40">
              <Globe className="size-3.5 text-accent-500" />
              <div className="font-mono text-small flex-1 truncate">{openSite.path}</div>
              {openSite.dirty && <span className="text-[10px] uppercase font-mono text-warning">несохранено</span>}
              <button
                type="button"
                onClick={saveConfig}
                disabled={busy === "save" || !openSite.dirty}
                className="px-2 py-1 rounded text-[11px] border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 disabled:opacity-40 inline-flex items-center gap-1"
              >
                {busy === "save" ? <Loader2 className="size-3 animate-spin" /> : <Save className="size-3" />}
                Сохранить + nginx -t
              </button>
              <button
                type="button"
                onClick={() => setOpenSite(null)}
                className="px-2 py-1 rounded text-[11px] border border-border/60 text-fg-muted hover:bg-bg-subtle"
              >
                Закрыть
              </button>
            </div>
            <div className="flex-1 min-h-0 bg-bg-subtle/30">
              <Suspense
                fallback={
                  <div className="p-4 text-fg-muted text-small">
                    <Loader2 className="size-4 animate-spin inline mr-2" /> загрузка редактора…
                  </div>
                }
              >
                <CodeMirrorEditor
                  content={openSite.content}
                  langKey="plain"
                  onChange={(val) =>
                    setOpenSite({ ...openSite, content: val, dirty: val !== openSite.original })
                  }
                />
              </Suspense>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LogsSection() {
  const [kind, setKind] = useState<"access" | "error">("error");
  const [n, setN] = useState(200);
  const [logs, setLogs] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      setLoading(true);
      setError(null);
      const r = await fetchNginxLog(kind, n);
      setLogs(r.logs);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [kind, n]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as "access" | "error")}
          className="px-2 py-1.5 rounded-md border border-border/60 bg-bg-card/70 text-small"
        >
          <option value="error">error.log</option>
          <option value="access">access.log</option>
        </select>
        <select
          value={n}
          onChange={(e) => setN(Number(e.target.value))}
          className="px-2 py-1.5 rounded-md border border-border/60 bg-bg-card/70 text-small"
        >
          <option value={50}>50</option>
          <option value={200}>200</option>
          <option value={1000}>1000</option>
          <option value={5000}>5000</option>
        </select>
        <button
          type="button"
          onClick={load}
          className="px-2 py-1.5 rounded-md border border-border/60 hover:bg-bg-subtle text-small text-fg-muted"
        >
          <RefreshCw className="size-3.5" />
        </button>
      </div>
      {error && <div className="rounded border border-danger/40 bg-danger/5 p-2 text-small text-danger">{error}</div>}
      <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden">
        <pre className="p-3 text-[11px] font-mono whitespace-pre-wrap leading-relaxed text-fg max-h-[70vh] overflow-auto">
          {loading ? (
            <Loader2 className="size-4 animate-spin inline" />
          ) : (
            logs || "(пусто)"
          )}
        </pre>
      </div>
    </div>
  );
}

function CertSection() {
  const [domain, setDomain] = useState("");
  const [email, setEmail] = useState("");
  const [staging, setStaging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ success: boolean; output: string } | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  async function run(withConfirm: boolean) {
    if (!domain.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await nginxCert({
        domain: domain.trim(),
        email: email.trim(),
        staging,
        confirm: withConfirm,
      });
      setResult(r);
      setConfirmed(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes(" 409 ") || msg.includes("certbot изменит")) {
        setConfirmed(true);
      } else {
        setResult({ success: false, output: msg });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 max-w-2xl">
      <div className="rounded-xl border border-border/60 bg-bg-card/40 p-4 space-y-2">
        <div className="text-small text-fg-muted">
          Получить Let's Encrypt сертификат для домена через certbot --nginx. Домен должен указывать
          на этот сервер по DNS. Certbot изменит nginx-конфиг сайта.
        </div>
        <input
          type="text"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="example.com"
          className="w-full px-3 py-2 rounded-md border border-border/60 bg-bg-card text-small font-mono"
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@example.com (опционально, для уведомлений LE)"
          className="w-full px-3 py-2 rounded-md border border-border/60 bg-bg-card text-small font-mono"
        />
        <label className="flex items-center gap-2 text-small text-fg-muted">
          <input type="checkbox" checked={staging} onChange={(e) => setStaging(e.target.checked)} />
          staging (тестовый CA, без rate-limit)
        </label>
        {!confirmed ? (
          <button
            type="button"
            onClick={() => run(false)}
            disabled={busy || !domain.trim()}
            className="px-3 py-2 rounded-md text-small font-medium text-white disabled:opacity-50"
            style={{ backgroundImage: "var(--grad-cta)" }}
          >
            {busy ? "Запускаю…" : "Получить сертификат"}
          </button>
        ) : (
          <div className="rounded border border-warning/40 bg-warning/5 p-3 space-y-2">
            <div className="text-small font-medium">Подтверди: certbot изменит nginx-конфиги.</div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => run(true)}
                disabled={busy}
                className="px-3 py-1.5 rounded-md text-small font-medium text-white"
                style={{ backgroundImage: "var(--grad-cta)" }}
              >
                {busy ? <Loader2 className="size-3 animate-spin inline" /> : "Подтверждаю"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmed(false)}
                className="px-3 py-1.5 rounded-md text-small border border-border/60"
              >
                Отмена
              </button>
            </div>
          </div>
        )}
      </div>
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className={cn(
            "rounded-xl border p-3",
            result.success ? "border-success/40 bg-success/5" : "border-danger/40 bg-danger/5",
          )}
        >
          <div className={cn("font-medium text-small mb-2", result.success ? "text-success" : "text-danger")}>
            {result.success ? "✓ Сертификат получен" : "✗ Ошибка"}
          </div>
          <pre className="text-[11px] font-mono whitespace-pre-wrap text-fg-muted max-h-[400px] overflow-auto">
            {result.output}
          </pre>
        </motion.div>
      )}
    </div>
  );
}
